import { useEffect, useContext } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Login from "./components/Auth/Login";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import Return from "./components/Auth/Return";
import Apps from "./components/Dashboard/Content/Apps/Apps";
import Assessments from "./components/Dashboard/Content/Assessments/Assessments";
import Reports from "./components/Dashboard/Content/Reports/Reports";
import UploadReport from "./components/Dashboard/Content/Reports/UploadReport";
import Report from "./components/Dashboard/Content/Reports/Report";
import Users from "./components/Dashboard/Content/Users/Users";
import AssignAssessment from "./components/Dashboard/Content/Assessments/AssignAssessment";
import SetNewPassword from "./components/Auth/SetNewPassword";
import HelpAndSupport from "./components/Dashboard/Content/HelpAndSupport/HelpAndSupport";
import AddUser from "./components/Dashboard/Content/Users/AddUser";
import EditUser from "./components/Dashboard/Content/Users/EditUser";
import UserGroups from "./components/Dashboard/Content/UserGroups/UserGroups";
import EditUserGroup from "./components/Dashboard/Content/UserGroups/EditUserGroup";
import AddUserGroup from "./components/Dashboard/Content/UserGroups/AddUserGroup";
import Billing from "./components/Dashboard/Content/Billing/Billing";
import Settings from "./components/Dashboard/Content/Settings/Settings";
import WelcomeNoAccess from "./components/Dashboard/WelcomeNoAccess";
import ErrorPage from "./components/ErrorPages/ErrorPage";
import Button from "./components/SharedComponents/Button";
import DashboardPlaceholder from "./components/SharedComponents/Loading/DashboardPlaceholder";

import { setSessionToken } from "./utils/api";
import { DashboardContext } from "./context/DashboardContext";

import timeIllustration from "./assets/images/time.svg";
import permissionsIllustration from "./assets/images/permissions.svg";
import notFoundIllustration from "./assets/images/404.svg";
import errorIllustration from "./assets/images/error.svg";

import "./index.css";
import EditHelpAndSupport from "./components/Dashboard/Content/HelpAndSupport/EditHelpAndSupport";
import ManageReport from "./components/Dashboard/Content/Reports/ManageReport";
import Notifications from "./components/Dashboard/Notifications/Notifications";
import AuthSSO from "./components/Auth/AuthSSO";
import RecommendedJob from "./components/Dashboard/Content/Reports/RecommendedJob";
import AuditLog from "./components/Dashboard/Content/AuditLogs/AuditLog";

function App() {
    const context = useContext(DashboardContext);
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const reason = params.get("reason");

        if (reason === "logout" || reason === "session-expired") {
            // force clear any stale token/session as previously it will re-use expired token
            setSessionToken(null);
            context.setLoggedIn(false);
            // clear the params from URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (sessionStorage.token) {
            context.setLoggedIn(true);
            setSessionToken(sessionStorage.token);
        } else {
            context.setLoggedIn(false);
        }
    }, [context, location.search]);

    const somethingHasGoneWrong = () => {
        return (
            <ErrorPage
                title={"Something has gone wrong on our end"}
                subtitle={
                    <div>
                        Please refresh the page or{" "}
                        <a className="text-primary-600 font-medium" href="mailto:techsupport@psychpress.com.au">
                            contact us{" "}
                        </a>
                        if this issue persists.
                    </div>
                }
                buttons={
                    <>
                        <Button
                            text="Previous page"
                            onClick={() => {
                                window.history.go(-1);
                            }}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                            }
                        />
                        {context.loggedIn ? (
                            <Button
                                text="Home"
                                onClick={() => {
                                    window.location.replace(context.home);
                                }}
                                disabled={context.permissionsLoading}
                                extraStyling={
                                    "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                    "focus:outline-none focus:ring-primary-600"
                                }
                            />
                        ) : (
                            <Button
                                text="Go to login"
                                onClick={() => {
                                    setSessionToken(null);
                                    context.setLoggedIn(false);
                                    window.location.replace("/login");
                                }}
                                extraStyling={
                                    "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                    "focus:outline-none focus:ring-primary-600"
                                }
                            />
                        )}
                    </>
                }
                illustration={errorIllustration}
            />
        );
    };

    const sessionExpired = () => {
        return (
            <ErrorPage
                title={"Looks like your session has expired or you haven't logged in."}
                subtitle={"Click the button below to Login again."}
                buttons={
                    <Button
                        text="Go to login"
                        onClick={() => {
                            setSessionToken(null);
                            context.setLoggedIn(false);
                            window.location.replace("/login");
                        }}
                        extraStyling={
                            "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                            "focus:outline-none focus:ring-primary-600"
                        }
                    />
                }
                illustration={timeIllustration}
            />
        );
    };

    const noPermission = () => {
        return (
            <ErrorPage
                title={"You do not have the neccesary permissions to view this page."}
                subtitle={"Contact your administrator if you think this is a mistake."}
                buttons={
                    <>
                        <Button
                            text="Previous page"
                            onClick={() => {
                                window.history.go(-2);
                            }}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                            }
                        />
                        <Button
                            text="Home"
                            onClick={() => {
                                window.location.replace(context.home);
                            }}
                            disabled={context.permissionsLoading}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                "focus:outline-none focus:ring-primary-600"
                            }
                        />
                    </>
                }
                illustration={permissionsIllustration}
            />
        );
    };

    const notFound = () => {
        return (
            <ErrorPage
                title={"We couldn't find the page you wanted to get to."}
                subtitle={
                    <div>
                        If you've typed this URL/website address please check for errors or
                        <a className={"text-primary-600 font-medium"} href="mailto:techsupport@psychpress.com.au">
                            {" "}
                            contact us{" "}
                        </a>{" "}
                        if you think this was our mistake.
                    </div>
                }
                buttons={
                    <>
                        <Button
                            text="Previous page"
                            onClick={() => {
                                window.history.go(-1);
                            }}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                            }
                        />
                        <Button
                            text="Home"
                            onClick={() => {
                                window.location.replace(context.home);
                            }}
                            disabled={context.permissionsLoading}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                "focus:outline-none focus:ring-primary-600"
                            }
                        />
                    </>
                }
                illustration={notFoundIllustration}
            />
        );
    };

    return (
        <div className="App">
            {context.loggedIn && context.home ? (
                <Routes>
                    <Route path="/" element={<Navigate to={context.home} />} />

                    {/*Main links*/}
                    <Route path="apps" element={<Apps />} />
                    <Route path="users" element={<Users />} />
                    <Route path="users/:searchTerm" element={<Users />} />
                    <Route path="users/add-user" element={<AddUser />} />
                    <Route path="users/edit-user" element={<EditUser />} />
                    <Route path="assessments" element={<Assessments />} />
                    <Route path="assessments/assign-assessment" element={<AssignAssessment />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="reports/:searchTerm" element={<Reports />} />
                    <Route path="reports/upload-report" element={<UploadReport />} />
                    <Route path="reports/manage-report" element={<ManageReport />} />
                    <Route path="report" element={<Report />} />

                    <Route path="recommended-job" element={<RecommendedJob />} />
                    <Route path="user-groups" element={<UserGroups />} />
                    <Route path="user-groups/:searchTerm" element={<UserGroups />} />
                    <Route path="user-groups/edit-user-group" element={<EditUserGroup />} />
                    <Route path="user-groups/add-user-group" element={<AddUserGroup />} />
                    <Route path="billing" element={<Billing />} />
                    <Route path="Notifications" element={<Notifications />} />
                    <Route path="audit-log" element={<AuditLog />} />

                    {/*Other links*/}
                    <Route path="settings" element={<Settings />} />
                    <Route path="help-and-support" element={<HelpAndSupport />} />
                    <Route path="help-and-support/edit-help-and-support" element={<EditHelpAndSupport />} />

                    {/*Auth*/}
                    <Route path="login/:authType/:authClient" element={<Login />} />
                    <Route path="login/tokenised/:usernameForToken/:token" element={<Login />} />
                    <Route path="login/:authType/:authClient/:usernameForToken/:token" element={<Login />} />

                    {/*Public*/}
                    <Route path="something-has-gone-wrong" element={somethingHasGoneWrong()} />
                    <Route path="session-expired" element={sessionExpired()} />
                    <Route path="welcome" element={<WelcomeNoAccess />} />
                    <Route path="no-permission" element={noPermission()} />
                    <Route path="*" element={notFound()} />
                    <Route path="placeholder" element={<DashboardPlaceholder />} />
                </Routes>
            ) : context.loggedIn && !context.home ? (
                <Routes>
                    {/*Placeholder while loading home page*/}
                    <Route path="*" element={<DashboardPlaceholder />} />
                </Routes>
            ) : (
                <Routes>
                    {/*Public*/}
                    <Route path="auth/sso" element={<AuthSSO />} />
                    <Route path="auth/sso/:authProvider" element={<AuthSSO />} />
                    <Route path="auth/sso/:authType/:authClient" element={<AuthSSO />} />
                    <Route path="auth/sso/:authProvider/:authType/:authClient" element={<AuthSSO />} />
                    <Route path="login/:authType/:authClient" element={<Login />} />
                    <Route path="login/tokenised/:usernameForToken/:token" element={<Login />} />
                    <Route path="login/:authType/:authClient/:usernameForToken/:token" element={<Login />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="set-up-password/:resetPasswordToken" element={<SetNewPassword />} />
                    <Route path="reset-password/:resetPasswordToken" element={<ResetPassword />} />
                    <Route path="return/:authType" element={<Return />}>
                        <Route path=":authClient" element={<Return />} />
                    </Route>
                    <Route path="something-has-gone-wrong" element={somethingHasGoneWrong()} />
                    <Route path="session-expired" element={sessionExpired()} />
                    <Route path="*" element={<Login />} />
                </Routes>
            )}
        </div>
    );
}

export default App;
